/**
 * Paths that must never produce GA4 / Meta traffic.
 *
 * These are logged-in, transactional or machine surfaces. Sessions there are
 * either our own team, a customer already past acquisition, or a crawler —
 * all of which distort acquisition reporting and conversion rates.
 *
 * Enforced in two places:
 *  1. `shouldLoadGtm()` / the GTM bootstrap snippet — the container never
 *     loads at all on these paths (covers direct entries and reloads).
 *  2. `pushExclusionFlag()` — on SPA navigation the container is already
 *     loaded, so we publish `truwe_excluded_path` to the dataLayer. GTM tags
 *     use it as a blocking-trigger exception.
 *
 * The same list is the source of truth for the `noindex` <meta> on those
 * routes and for the Disallow rules in robots.txt.
 */
export const GA_EXCLUDED_PATH_PREFIXES = [
  "/admin",
  "/auth",
  "/account",
  "/api",
  "/quiz",
  "/diet-plan",
] as const;

export function isExcludedPath(pathname: string | undefined | null): boolean {
  if (!pathname) return false;
  let path = pathname.toLowerCase().trim();
  if (!path.startsWith("/")) path = `/${path}`;
  return GA_EXCLUDED_PATH_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`),
  );
}

/**
 * Publish the current path's exclusion state to the dataLayer so GTM can gate
 * tags that fire on SPA history changes (the container cannot be unloaded
 * once it has booted).
 */
export function pushExclusionFlag(pathname: string): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ truwe_excluded_path: isExcludedPath(pathname) });
}
